// RDx Decks V2 — the vanilla slide renderer.
//
// Compiles a SlideDeck (slide-model.ts) into self-contained `.slide` markup,
// ported branch-for-branch from the prototype `slide-render.jsx` Slide(). The
// output is framework-free: it carries `data-region` (the inline-edit contract)
// and `data-build` (present-mode reveal steps); animation/scale/present/nav are
// driven by the runtime (slide-stage.js). Charts/trends compute their SVG
// geometry statically here so the slide is correct without JS; the runtime only
// animates the reveal.
//
// XSS posture mirrors the legacy render-deck.ts: every text field is escaped;
// titles convert "\n" to <br>; image srcs go through srcAttr; art/variant/align
// are allow-listed. Field values are stored as plain text in the model (inline
// edits are committed into fields on accept), so no raw HTML field is trusted.

import type {
  Slide, SlideDeck, ArtKind, Stat, TrendPoint, ChartBar, SlideBackground,
  DeckMeta, DeckBrand,
} from './slide-model';
import { slideSteps, normaliseSlide, safeLayout, safeFont } from './slide-model';
import { layoutIdsFor } from './kind-meta';
import { THEME_IDS } from './themes';

// ── escaping + helpers ────────────────────────────────────────────────────
export function esc(v: unknown): string {
  return String(v ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
/** Escape, then turn newlines into <br> (titles / cover headlines). */
function escBr(v: unknown): string {
  return esc(v).replace(/\n/g, '<br>');
}
/** Wrap the (escaped) accent substring of an (escaped) value in .accent. */
function accentWrap(value: string, accent?: string): string {
  const e = esc(value);
  if (!accent) return e;
  const ea = esc(accent);
  return e.includes(ea) ? e.replace(ea, `<span class="accent">${ea}</span>`) : e;
}
const ART_KINDS = new Set<ArtKind>(['slate', 'sage', 'dawn', 'mist', 'dusk', 'ember', 'ocean', 'plum', 'gold', 'forest']);
function artClass(art?: ArtKind): string { return art && ART_KINDS.has(art) ? art : 'slate'; }
function srcAttr(src?: string): string {
  if (!src) return '';
  const raw = String(src);
  // eslint-disable-next-line no-control-regex
  const probe = raw.replace(/[\x00- ]/g, '').toLowerCase();
  const ok = probe.startsWith('/') || probe.startsWith('http://') || probe.startsWith('https://')
    || probe.startsWith('data:image/') || !probe.includes(':');
  return ok ? esc(raw.trim()) : '';
}

/** A RESOLVED, renderable image src — `srcAttr` minus the unresolved generation
 *  sentinel. An `img` of the form `generate:…` is a STORED *request* for an image
 *  the bulk generator hasn't baked yet (the studio writes it; the server later
 *  swaps in a real `/api/images/<id>` URL). It is NOT a real image, so it must
 *  never flip a slide into media/has-bg mode (CONTRACT 5) — that yields white
 *  text over a non-existent backdrop. Returns '' for an unresolved `generate:`
 *  src (and for any src `srcAttr` would reject), so callers can treat the slot
 *  as empty (gradient/placeholder) until the image is resolved. */
export function resolvedSrc(src?: string): string {
  if (!src) return '';
  if (/^generate:/i.test(String(src).trim())) return '';
  return srcAttr(src);
}

/** A `background-position` value from an optional focal point (percent, 0–100).
 *  Defaults to `center` when unset/invalid so off-centre subjects can be reframed
 *  without disturbing the byte-identical centred default. */
function focalPos(focal?: { x?: number; y?: number }): string {
  if (!focal) return 'center';
  const clamp = (v: unknown): number | null => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : null;
  };
  const x = clamp(focal.x);
  const y = clamp(focal.y);
  if (x == null && y == null) return 'center';
  return `${x ?? 50}% ${y ?? 50}%`;
}

// Scrim weights a media background may carry. 'bottom' = a foot-up gradient,
// 'centre' = a radial darken under centred text, 'strong' = full-coverage,
// 'none' = no veil. The renderer emits one of `scrim-bottom`/`scrim-centre`/
// `scrim-strong` (BG-CSS owns the styling); 'none' emits no scrim element.
type ScrimWeight = 'bottom' | 'centre' | 'strong' | 'none';
const SCRIM_WEIGHTS = new Set<ScrimWeight>(['bottom', 'centre', 'strong', 'none']);
/** Resolve the scrim weight for a media background (CONTRACT 4). When the author
 *  set one we honour it verbatim; UNSET defaults to 'strong' for image/image-split
 *  on text-bearing kinds (so text never sits unveiled over a photo) and 'bottom'
 *  for gradient/motion/video (the legacy foot veil). */
function scrimWeight(bg: SlideBackground, textBearing: boolean): ScrimWeight {
  const set = bg.scrim;
  if (set && SCRIM_WEIGHTS.has(set as ScrimWeight)) return set as ScrimWeight;
  const isImage = bg.type === 'image' || bg.type === 'image-split';
  return isImage && textBearing ? 'strong' : 'bottom';
}
/** The scrim element for a media background, or '' for weight 'none'. `cls` lets
 *  a kind keep its bespoke scrim class (e.g. `fb-scrim`, `qi-scrim`, `bg-scrim`)
 *  while ALSO carrying the weight modifier so BG-CSS can target it. */
function scrimEl(weight: ScrimWeight, cls = 'slide-scrim'): string {
  if (weight === 'none') return '';
  return `<div class="${cls} scrim-${weight}"></div>`;
}

// ── rich-text sanitiser (the one mandatory XSS boundary) ───────────────────
// Some override regions (titles/ledes/quotes/bullets — see RICH_REGIONS) now
// carry INLINE rich text authored in a contenteditable: bold/italic/underline/
// strike, links, and a colour span. That markup reaches ANONYMOUS viewers in a
// `sandbox="allow-scripts"` iframe (baked deck.html + live renderSlidesDoc), so
// sanitizeRich is the airtight allowlist that everything passes through. It is
// hand-rolled (zero deps), idempotent, and strips EVERYTHING not allow-listed
// while keeping the text content — so legacy plain text (e.g. "5 < 10") and any
// disallowed-tag inner text survive as literal, escaped characters.

/** Safe-scheme check for an `<a href>` — reuses the SAME allow-list as srcAttr
 *  (the image-src guard): only `/`, http(s):, mailto:, tel:, and schemeless URLs
 *  pass; javascript:, data:text/html, vbscript:, etc. are dropped. */
function hrefAttr(href: string): string {
  const raw = String(href);
  // eslint-disable-next-line no-control-regex
  const probe = raw.replace(/[\x00- ]/g, '').toLowerCase();
  const ok = probe.startsWith('/') || probe.startsWith('#')
    || probe.startsWith('http://') || probe.startsWith('https://')
    || probe.startsWith('mailto:') || probe.startsWith('tel:')
    || !probe.includes(':');
  return ok ? esc(raw.trim()) : '';
}

/** A CSS colour value safe to inline in `style="color:…"`. Allows hex, rgb()/
 *  rgba()/hsl()/hsla() with only numeric/percent/comma/space/dot args, and a
 *  conservative set of CSS named colours. Anything containing url(), expression(),
 *  a semicolon, braces, or other punctuation is rejected (returns ''). */
const NAMED_COLOURS = new Set([
  'black', 'silver', 'gray', 'grey', 'white', 'maroon', 'red', 'purple', 'fuchsia',
  'green', 'lime', 'olive', 'yellow', 'navy', 'blue', 'teal', 'aqua', 'orange',
  'aliceblue', 'aquamarine', 'azure', 'beige', 'bisque', 'brown', 'chocolate',
  'coral', 'crimson', 'cyan', 'darkblue', 'darkgreen', 'darkgray', 'darkgrey',
  'darkred', 'gold', 'indigo', 'ivory', 'khaki', 'lavender', 'magenta', 'pink',
  'salmon', 'sienna', 'tan', 'tomato', 'turquoise', 'violet', 'wheat',
  'transparent', 'currentcolor',
]);
function safeColour(v: string): string {
  const c = v.trim().toLowerCase();
  if (!c) return '';
  if (/^#[0-9a-f]{3,8}$/.test(c)) return c;
  if (/^rgba?\([\d.,\s%]+\)$/.test(c)) return c;
  if (/^hsla?\([\d.,\s%]+\)$/.test(c)) return c;
  if (NAMED_COLOURS.has(c) && /^[a-z]+$/.test(c)) return c;
  return '';
}

/** Pull a single safe `color: <value>` out of a raw style attribute string,
 *  ignoring every other property. Returns '' if no safe colour is present. */
function safeColourStyle(style: string): string {
  // split on ; and find the first `color:` declaration (not background-color etc.)
  for (const decl of style.split(';')) {
    const idx = decl.indexOf(':');
    if (idx < 0) continue;
    const prop = decl.slice(0, idx).trim().toLowerCase();
    if (prop !== 'color') continue;
    const col = safeColour(decl.slice(idx + 1));
    if (col) return col;
  }
  return '';
}

// Self-closing / void allowed marks (only <br>); the rest are paired marks.
const RICH_VOID = new Set(['br']);
// Inline marks that pass through verbatim (no attributes kept).
const RICH_MARKS = new Set(['b', 'strong', 'i', 'em', 'u', 's']);

/** Hand-rolled allow-list sanitiser for inline rich text. Allowed: `b strong i
 *  em u s br`, `a` with a safe-scheme `href`, and `span` with ONLY a safe
 *  `style="color:…"`. Every other tag is dropped but its text content kept; any
 *  bare `<`/`>`/`&` that is not part of an allowed tag is escaped, so plain text
 *  with comparison operators stays literal. Idempotent. */
export function sanitizeRich(html: string): string {
  const input = String(html ?? '');
  let out = '';
  let i = 0;
  const n = input.length;
  while (i < n) {
    const ch = input[i];
    if (ch === '<') {
      // Try to parse a tag starting here. A valid tag is `<`, optional `/`, a
      // letter, then anything up to the next `>`. If there's no closing `>` (an
      // unclosed/truncated tag) we escape the lone `<` and move on.
      const close = input.indexOf('>', i);
      const m = /^<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)/.exec(input.slice(i));
      if (close === -1 || !m) {
        out += '&lt;';
        i += 1;
        continue;
      }
      const tagInner = input.slice(i, close + 1); // includes < … >
      const isClose = m[1] === '/';
      const tag = m[2].toLowerCase();
      i = close + 1; // advance past this tag regardless of whether it's allowed

      if (RICH_VOID.has(tag)) { out += '<br>'; continue; }
      if (RICH_MARKS.has(tag)) { out += isClose ? `</${tag}>` : `<${tag}>`; continue; }
      if (tag === 'a') {
        if (isClose) { out += '</a>'; continue; }
        const href = /\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+))/i.exec(tagInner);
        const rawHref = href ? (href[2] ?? href[3] ?? href[4] ?? '') : '';
        const safe = hrefAttr(rawHref);
        // keep the anchor element so the link text stays styled; drop a bad href
        out += safe ? `<a href="${safe}">` : '<a>';
        continue;
      }
      if (tag === 'span') {
        if (isClose) { out += '</span>'; continue; }
        const styleM = /\bstyle\s*=\s*("([^"]*)"|'([^']*)')/i.exec(tagInner);
        const rawStyle = styleM ? (styleM[2] ?? styleM[3] ?? '') : '';
        const col = rawStyle ? safeColourStyle(rawStyle) : '';
        out += col ? `<span style="color:${col}">` : '<span>';
        continue;
      }
      // Disallowed tag: drop the tag entirely (its inner text is preserved as we
      // continue scanning the following characters).
      continue;
    }
    if (ch === '>') { out += '&gt;'; i += 1; continue; }
    if (ch === '&') {
      // Preserve a well-formed entity; escape a bare ampersand.
      const ent = /^&(#\d+|#x[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/.exec(input.slice(i));
      if (ent) { out += ent[0]; i += ent[0].length; continue; }
      out += '&amp;';
      i += 1;
      continue;
    }
    out += ch;
    i += 1;
  }
  return out;
}

/** Region ids that opt INTO inline rich text. Membership is by exact id, except
 *  `bullet-*` which matches by prefix (bullet-0, bullet-1, …). Everything not in
 *  this set keeps the legacy plain-text escape contract verbatim. Exported so the
 *  Studio's inline editor can decide whether to commit sanitised innerHTML (rich)
 *  or plain innerText (non-rich) — `isRichRegion` is the authoritative test
 *  because it also handles the `bullet-*` prefix that the bare Set cannot. */
export const RICH_REGIONS = new Set(['title', 'lede', 'quote', 'eyebrow', 'sub']);
export function isRichRegion(id: string): boolean {
  return RICH_REGIONS.has(id) || id.startsWith('bullet-');
}

// Inline-edit overrides for the slide currently being rendered. Set at the top
// of renderSlide (rendering is synchronous + single-threaded, so a module-level
// value is safe and avoids threading the map through every reg() call). Values
// are PLAIN TEXT for non-rich regions (escaped on output) and sanitised inline
// rich text for RICH_REGIONS — so an override can never inject markup into a
// shared deck.
let _ov: Record<string, string> = {};
/** Per-region presentation styles (heading size + alignment) for the slide
 *  currently being rendered — a parallel channel to _ov, applied by reg()/er()
 *  as `rt-h{n}` / `rt-align-{x}` classes (NOT stored as tags). */
let _rs: Record<string, RegionStyle> = {};
/** Override HTML for region `id`: sanitised rich text for a rich region,
 *  escaped-with-<br> plain text otherwise; or the fallback when no override. */
function ovHtml(id: string, fallback: string): string {
  const v = _ov[id];
  if (v == null) return fallback;
  return isRichRegion(id) ? sanitizeRich(v) : esc(v).replace(/\n/g, '<br>');
}

/** The extra class tokens (rich-text heading size + alignment) for a region, or
 *  '' when none are set. Appended to the element's existing class list. */
function rsClass(id: string): string {
  const rs = _rs[id];
  if (!rs) return '';
  const parts: string[] = [];
  if (rs.h === 1 || rs.h === 2 || rs.h === 3) parts.push(`rt-h${rs.h}`);
  if (rs.align === 'left' || rs.align === 'center' || rs.align === 'right') parts.push(`rt-align-${rs.align}`);
  return parts.join(' ');
}
/** Merge a base class string with the region's rt-* style classes. */
function withRs(id: string, base: string): string {
  const extra = rsClass(id);
  return extra ? (base ? `${base} ${extra}` : extra) : base;
}
/** A ` class="…"` attribute for a directly-emitted rich element (the title h1s,
 *  blockquotes, cta sub/headline) that bypass reg()/er(); merges the element's
 *  fixed class with the region's rt-* style classes. */
function rcls(id: string, base: string): string {
  const finalCls = withRs(id, base);
  return finalCls ? ` class="${finalCls}"` : '';
}

/** A `data-region` editable element. Applies an inline-edit override when set. */
function reg(id: string, base: string, html: string): string {
  const finalCls = withRs(id, base);
  const cls = finalCls ? ` class="${finalCls}"` : '';
  return `<${'div'}${cls} data-region="${esc(id)}">${ovHtml(id, html)}</div>`;
}
/** Editable element with an arbitrary tag (span/div/li…) — same data-region +
 *  override contract as reg(), used to make every text field editable without
 *  changing the element's tag/class (the studio's wireRegions picks up any
 *  [data-region]). */
function er(tag: string, id: string, cls: string, html: string): string {
  const finalCls = withRs(id, cls);
  const c = finalCls ? ` class="${finalCls}"` : '';
  return `<${tag}${c} data-region="${esc(id)}">${ovHtml(id, html)}</${tag}>`;
}
/** Build-step wrapper attribute (present-mode reveal index). */
function build(idx: number): string { return ` data-build="${idx}"`; }

const ART_GLYPH = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.6"/><path d="M21 15l-5-5L5 20"/></svg>';
const FEATURE_ICONS: Record<string, string> = {
  home: '<path d="M3 9l9-6 9 6v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/><path d="M9 21V12h6v9"/>',
  trending: '<path d="M4 19V5M4 19h16"/><path d="M8 16l4-5 3 3 5-7"/>',
  shield: '<path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"/>',
};
const CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 6"/></svg>';
const CROSS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

// A YouTube id is exactly 11 chars from the URL-safe alphabet. We re-validate on
// EMISSION (never trust the stored value) so a malformed id can never reach the
// poster URL or the data-video-id the player bridge reads.
const YT_ID_EMIT_RE = /^[A-Za-z0-9_-]{11}$/;
function safeVideoId(id?: string): string {
  const v = String(id ?? '').trim();
  return YT_ID_EMIT_RE.test(v) ? v : '';
}
/** The poster + play affordance for a video background. We deliberately emit NO
 *  cross-origin <iframe> in the static markup — only the YouTube THUMBNAIL image
 *  (which is a plain <img>, safe in the sandboxed share/live viewer) plus a play
 *  glyph. The player bridge in slide-stage.js mounts a real YT player OVER this
 *  poster in NON-sandboxed contexts (editor focus-host + studio Present); in the
 *  hermetic share/live iframe (sandbox without allow-same-origin) the poster is
 *  the final state. `data-video-id` is the bridge's hook. */
function videoPoster(videoId: string): string {
  const id = safeVideoId(videoId);
  if (!id) return '';
  return `<img class="video-poster" src="https://img.youtube.com/vi/${id}/hqdefault.jpg" alt="" loading="lazy" decoding="async">` +
    `<span class="video-play" aria-hidden="true"><svg viewBox="0 0 68 48"><path d="M66.5 7.5a8 8 0 0 0-5.6-5.7C56 .5 34 .5 34 .5S12 .5 7.1 1.8A8 8 0 0 0 1.5 7.5C.2 12.4.2 24 .2 24s0 11.6 1.3 16.5a8 8 0 0 0 5.6 5.7C12 47.5 34 47.5 34 47.5s22 0 26.9-1.3a8 8 0 0 0 5.6-5.7C67.8 35.6 67.8 24 67.8 24s0-11.6-1.3-16.5z" fill="rgba(20,24,31,0.82)"/><path d="M27 34V14l18 10z" fill="#fff"/></svg></span>`;
}

function art(a: { art?: ArtKind; img?: string; videoId?: string; animate?: boolean; className?: string; noGlyph?: boolean; region?: string; focal?: { x?: number; y?: number } }): string {
  const vid = safeVideoId(a.videoId);
  // RESOLVE the src up front: an unresolved `generate:…` request (or any src the
  // renderer would reject) counts as NO image, so the slot stays a gradient/
  // placeholder rather than a broken background-image (CONTRACT 5).
  const img = !vid ? resolvedSrc(a.img) : '';
  const hasImg = Boolean(img);
  // An empty FILLABLE slot (carries a region, no resolved image, no video) gets
  // the `img-empty` marker so BG-CSS can render the visible placeholder tile
  // (CONTRACT 3); a resolved image gets `has-img`. A region-less decorative art
  // layer (no fillable affordance) gets neither.
  const stateCls = a.region ? (hasImg ? ' has-img' : ' img-empty') : (hasImg ? ' has-img' : '');
  const cls = 'art' + (vid ? ' bg-video' : '') + (a.animate && !vid ? ' kenburns' : '') + stateCls + (a.className ? ' ' + a.className : '');
  const layerStyle = hasImg
    ? ` style="background-image:url(${img});background-size:cover;background-position:${focalPos(a.focal)}"`
    : '';
  // The glyph is an "add an image here" placeholder — only meaningful on a truly
  // EMPTY slot (no image AND no chosen gradient). A picked gradient is an
  // intentional choice, so it shows no icon; the editor adds a "change" cue via
  // the data-img-region hover affordance instead.
  const glyph = (vid || hasImg || a.art || a.noGlyph) ? '' : `<span class="art-glyph">${ART_GLYPH}</span>`;
  // data-img-region makes the slot click-to-edit in the Studio (parallels the
  // text data-region contract); it is inert in present/share. data-video-id is
  // the player bridge's hook (slide-stage.js).
  const regionAttr = a.region ? ` data-img-region="${esc(a.region)}"` : '';
  const videoAttr = vid ? ` data-video-id="${vid}"` : '';
  // For a video slot the art-layer holds the poster (a plain YouTube thumbnail
  // <img> + play glyph), NOT a gradient/image-url; everything else is unchanged.
  const layerInner = vid ? videoPoster(vid) : '';
  return `<div class="${cls}"${regionAttr}${videoAttr}><div class="art-layer${vid ? ' video-layer' : ' art-' + artClass(a.art)}"${layerStyle}>${layerInner}</div>${glyph}</div>`;
}

/** The decorative side-rail for the `*-with-image` / `side-panel` layouts. Wraps
 *  an art() layer in a CSS-targeted container (`.kpi-media`, `.bul-media`, …) —
 *  the panel media is the NEW optional `panelImg`/`panelArt` fields, decorative
 *  and so carrying NO data-region (mirrors split/gallery's art() usage). When no
 *  panel image is set it falls back to a tinted gradient art layer, so the rail
 *  always reads as an intentional composition. */
function sidePanel(cls: string, s: { panelImg?: string; panelArt?: ArtKind; animate?: boolean }): string {
  return `<div class="${cls}">${art({ art: s.panelArt, img: s.panelImg, animate: s.animate, noGlyph: !s.panelImg, region: 'panel' })}</div>`;
}

// ── unified background ──────────────────────────────────────────────────────
// One source of truth for a slide's background: the structured `background`
// field, falling back to the legacy top-level art/img so stored decks render
// unchanged. Every kind reads this; the studio writes it.
function resolveBackground(s: Slide): SlideBackground {
  const b = (s as { background?: SlideBackground }).background;
  if (b && b.type) return b;
  const lart = (s as { art?: ArtKind }).art;
  const limg = (s as { img?: string }).img;
  if (limg) return { type: 'image', img: limg };
  if (lart) return { type: 'gradient', art: lart };
  return { type: 'none' };
}
/** A full-bleed backdrop for ANY kind (the generic path for the data/card kinds
 *  that have no bespoke media). colour = a brand/theme-aware wash (no scrim,
 *  text stays normal); gradient/image/motion/video = an art layer + a scrim
 *  whose weight honours bg.scrim (CONTRACT 4; text flips to on-media via
 *  .bg-media). `image-split` renders a real split frame honouring bg.side
 *  (CONTRACT 6) rather than a full-bleed image. */
function slideBg(bg: SlideBackground): string {
  if (bg.type === 'colour') {
    // The colour wash consumes --bg-tint (CONTRACT 6): strength of the brand/
    // theme wash painted over the canvas. BG-CSS reads the var off .bg-wash.
    const tint = Math.max(0, Math.min(100, bg.tint ?? 14));
    return `<div class="slide-bg bg-wash" style="--bg-tint:${tint}%"></div>`;
  }
  if (bg.type === 'video') {
    // Emit the poster-only backdrop (NO cross-origin iframe in static markup);
    // the bridge mounts a real player over it in non-sandboxed contexts.
    return art({ videoId: bg.videoId, className: 'slide-bg', noGlyph: true }) + scrimEl(scrimWeight(bg, true));
  }
  if (bg.type === 'image-split') {
    // A real split frame: the media plate occupies one half (bg.side, default
    // left), the other half is the bare canvas the slide's content sits over.
    // The scrim lives ON the media plate so the bare half stays clean.
    const side = bg.side === 'right' ? 'right' : 'left';
    const plate = art({ art: bg.art, img: bg.img, focal: bg.focal, className: 'split-plate', region: 'bg' }) + scrimEl(scrimWeight(bg, true), 'split-plate-scrim');
    return `<div class="slide-bg bg-split bg-split-${side}">${plate}</div>`;
  }
  // gradient / image / motion: a single full-bleed art layer. Only a real image
  // takes a focal point; a gradient ignores it. The `image` type is a fillable
  // slot (region:'bg' → click-to-edit + placeholder glyph when empty, CONTRACT
  // 2/3); gradient/motion are decorative (no region, no glyph).
  const isImage = bg.type === 'image';
  return art({ art: bg.art, img: bg.img, focal: bg.focal, animate: bg.type === 'motion', className: 'slide-bg', noGlyph: !isImage, ...(isImage ? { region: 'bg' } : {}) }) + scrimEl(scrimWeight(bg, true));
}
/** Kinds with bespoke media handling (their own scrim/positioning); the generic
 *  full-bleed backdrop is NOT added for these (Phase 4 unifies them via frames). */
const MEDIA_KINDS = new Set(['cover', 'cta', 'full-bleed', 'quote-image', 'split']);

// ── layout axis ─────────────────────────────────────────────────────────────
// Each slide emits `k-<kind> lay-<layout>` (+ data-layout) so CSS can target a
// named layout per kind. layoutOf falls back to the kind's first catalogue
// layout (= the current look) for any slide without an explicit/valid layout,
// so stored decks render unchanged. skin (s-ink/s-surface) stays a separate
// axis on the legacy `variant` class.
function layoutOf(s: Slide): string {
  const l = (normaliseSlide(s) as { layout?: string }).layout;
  return safeLayout(l, layoutIdsFor(s.kind));
}
/** Cover layouts that map onto the legacy `cover--<style>` CSS; the rest layer
 *  their `.lay-<id>` delta on top of the classic base. */
const COVER_STYLE_LAYOUTS = new Set(['classic', 'statement', 'editorial']);
const coverStyleFor = (layout: string): string => (COVER_STYLE_LAYOUTS.has(layout) ? layout : 'classic');
/** Title visibility — the per-slide `show.title` toggle (default on). */
function showTitle(s: Slide): boolean {
  return (s as { show?: { title?: boolean } }).show?.title !== false;
}

// ── image-slot manifest (the authoritative `data-img-region` list) ──────────
// imageSlotsOf returns EXACTLY the `data-img-region` values renderSlide emits
// for a slide, in emission order. It is the single source of truth for the
// whole-deck "Generate images" pass (studio-page.ts) — KIND_META only knows a
// kind's bg CAPABILITY, not which slots actually render (panel/gallery are
// layout- and array-dependent). This function MUST move in lockstep with the
// art()/sidePanel() call sites in renderSlide: any change to which kinds/layouts
// emit a region (or to the region naming) has to be reflected here, and the
// render-slides.test.ts cases below pin both together.
export function imageSlotsOf(slide: Slide): string[] {
  // Coerce first so a PARTIAL gallery slide (no items[]) yields an empty slot
  // list instead of throwing on `s.items.map` — keeps the studio's whole-deck
  // image pass resilient to a slide cut off mid-generation.
  const s = normaliseSlide(slide);
  const k = s.kind;
  const layout = layoutOf(s);
  // The generic full-bleed backdrop (slideBg) emits a `bg` slot when a NON-media
  // kind carries an actual image / image-split background. This mirrors the
  // `isImage`/`image-split` region:'bg' emission in slideBg() so the studio can
  // target a data kind's photo background. Resolved here once for the default
  // branch + the panelled kinds (a with-image panel AND an image bg can coexist).
  const genericBgSlot = (): string[] => {
    if (MEDIA_KINDS.has(k)) return [];
    const t = resolveBackground(s).type;
    return (t === 'image' || t === 'image-split') ? ['bg'] : [];
  };
  switch (k) {
    // cover: the `bg` slot exists in the IMAGE-CAPABLE layouts — with-image
    // (full-bleed bgLayer) + side-panel / split-portrait (the .cover-panel
    // art()). A plain/classic/statement/editorial/centred cover is a text-flow
    // composition whose backdrop is decorative gradient only → NOT a slot.
    case 'cover':
      return (layout === 'with-image' || layout === 'side-panel' || layout === 'split-portrait') ? ['bg'] : [];
    // split / full-bleed / quote-image: always emit a single `bg` media slot.
    case 'split':
    case 'full-bleed':
    case 'quote-image':
      return ['bg'];
    // agenda / bullets / kpi: the side-rail panel slot exists ONLY in the
    // `with-image` layout (sidePanel() → region:'panel'); on any layout an
    // image-type background ALSO contributes a generic `bg` slot.
    case 'agenda':
    case 'bullets':
    case 'kpi':
      return [...(layout === 'with-image' ? ['panel'] : []), ...genericBgSlot()];
    // gallery: one `gal:<i>` slot per items[] entry (its backgrounds are never
    // image-typed, so no generic `bg`).
    case 'gallery':
      return (s.items || []).map((_, i) => `gal:${i}`);
    // cta's bgLayer carries no data-img-region. Every other data kind exposes a
    // `bg` slot only when its background is image-typed.
    default:
      return genericBgSlot();
  }
}

// ── chart / trend / spark geometry (static; runtime animates) ─────────────
function chartHtml(bars: ChartBar[], unit = '', source?: string): string {
  const list = bars || [];
  const max = Math.max(...list.map((b) => b.v), 1);
  const rows = list.map((b, i) =>
    `<div class="chart-row" data-i="${i}" data-note="${esc(b.note ?? '')}">` +
    er('span', `chart-${i}-k`, 'chart-k', esc(b.k)) +
    `<span class="chart-track"><span class="chart-fill" data-w="${(b.v / max * 100).toFixed(2)}" style="transition-delay:${i * 70}ms"></span></span>` +
    `<span class="chart-v t-num">${esc(b.v)}${esc(unit)}</span></div>`).join('');
  const cap = source ? `<span class="faint">Source: ${esc(source)}</span>` : 'Hover a bar to read it.';
  return `<div class="chart"><div class="chart-bars">${rows}</div><div class="chart-cap" data-default="${esc(cap)}">${cap}</div></div>`;
}
function trendHtml(s: Extract<Slide, { kind: 'trend' }>): string {
  const pts = s.points || [];
  // A PARTIAL trend (no points — e.g. a slide cut off mid-generation) renders an
  // empty trend frame rather than throwing on `pts[pts.length - 1].x`.
  if (pts.length === 0) return `<div class="trend"></div>`;
  const W = 1560, H = 470, padX = 30, padB = 56, padT = 20;
  const vals = pts.map((p) => p.v);
  const max = s.max != null ? s.max : Math.max(...vals) * 1.12;
  const min = s.min != null ? s.min : Math.min(0, Math.min(...vals));
  const X = (i: number) => padX + (i / (pts.length - 1)) * (W - padX * 2);
  const Y = (v: number) => padT + (1 - (v - min) / (max - min || 1)) * (H - padT - padB);
  const line = pts.map((p, i) => (i ? 'L' : 'M') + X(i).toFixed(1) + ',' + Y(p.v).toFixed(1)).join(' ');
  const area = line + ` L${X(pts.length - 1).toFixed(1)},${H - padB} L${X(0).toFixed(1)},${H - padB} Z`;
  const gridYs = [0, 0.5, 1].map((f) => padT + f * (H - padT - padB));
  const grid = gridYs.map((y) => `<line class="cl-grid" x1="${padX}" y1="${y}" x2="${W - padX}" y2="${y}" stroke="var(--slide-border)" stroke-width="1.5"/>`).join('');
  const dots = pts.map((p: TrendPoint, i) =>
    `<circle class="trend-dot" data-i="${i}" data-x="${esc(p.x)}" data-v="${esc((s.prefix || '') + p.v + (s.unit || ''))}" data-note="${esc(p.note ?? '')}" cx="${X(i)}" cy="${Y(p.v)}" r="7" fill="var(--slide-canvas)" stroke="var(--slide-accent)" stroke-width="4"/>`).join('');
  const last = pts[pts.length - 1];
  return `<div class="trend">` +
    `<div class="trend-read"><span class="trend-x">${esc(last.x)}</span><span class="trend-v t-num">${esc((s.prefix || '') + last.v + (s.unit || ''))}</span>${last.note ? `<span class="trend-note faint">${esc(last.note)}</span>` : ''}</div>` +
    `<svg class="trend-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none">` +
    `<defs><linearGradient id="tg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="var(--slide-accent)" stop-opacity="0.28"/><stop offset="100%" stop-color="var(--slide-accent)" stop-opacity="0"/></linearGradient></defs>` +
    grid +
    `<path class="trend-area" d="${area}" fill="url(#tg)"/>` +
    `<path class="trend-line" d="${line}" fill="none" stroke="var(--slide-accent)" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" pathLength="1"/>` +
    dots + `</svg>` +
    `<div class="trend-axis">${pts.map((p, i) => er('span', `trend-${i}-x`, '', esc(p.x))).join('')}</div></div>`;
}
function sparkHtml(data: number[], up?: boolean): string {
  const W = 132, Ht = 40, min = Math.min(...data), max = Math.max(...data);
  const pts = data.map((v, i) => [i / (data.length - 1) * W, max === min ? Ht / 2 : Ht - 4 - (v - min) / (max - min) * (Ht - 8)]);
  const line = 'M' + pts.map((p) => p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' L');
  const area = line + ` L${W},${Ht} L0,${Ht} Z`;
  const col = up ? 'var(--success-strong)' : 'var(--error-strong)';
  const lp = pts[pts.length - 1];
  return `<svg class="spark" viewBox="0 0 ${W} ${Ht}" preserveAspectRatio="none"><path d="${area}" fill="${col}" opacity="0.12"/><path d="${line}" fill="none" stroke="${col}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/><circle cx="${lp[0]}" cy="${lp[1]}" r="3.4" fill="${col}"/></svg>`;
}

function titleBlock(s: Slide, opts: { accentEyebrow?: boolean; tight?: boolean; lede?: string }): string {
  const eb = s.eyebrow != null ? reg('eyebrow', 'eyebrow' + (opts.accentEyebrow ? ' accent' : ''), esc(s.eyebrow)) : '';
  const t = showTitle(s) ? reg('title', 'title', escBr(s.title)) : '';
  const lede = opts.lede ? reg('lede', 'lede', esc(opts.lede)) : '';
  return `<div class="title-block${opts.tight ? ' tight' : ''}"${build(0)}>${eb}${t}${lede}</div>`;
}

// ── the renderer ──────────────────────────────────────────────────────────
const NO_FOOT = new Set(['cover', 'cta', 'full-bleed', 'split', 'quote-image', 'trend']);
/** Safe empty column for a `compare` slide whose left/right is missing — mirrors
 *  slide-model's backfill so a partial compare slide renders two blank columns
 *  rather than throwing on `c.title`/`c.items`. */
const EMPTY_COMPARE_COL = { tag: '', title: '', items: [] as string[] };

/** Render one slide to its `.slide` markup string. `meta` (optional) carries the
 *  deck-level brand kit so the cover wordmark + the per-slide brand footer can be
 *  emitted; when absent (e.g. the Studio focus editor before a deck is threaded)
 *  the cover falls back to the literal "RICOH RDx" mark and no footer logo. */
export function renderSlide(
  slide: Slide,
  overrides: Record<string, string> = {},
  meta?: DeckMeta,
  regionStyles: Record<string, RegionStyle> = {},
): string {
  _ov = overrides;
  _rs = regionStyles;
  // Coerce the slide UP FRONT so every downstream branch reads a coherent shape:
  // normaliseSlide backfills each kind's required array (and compare's columns) to
  // a safe empty value when missing. A PARTIAL slide (one emitted during a
  // generation error without its content array) therefore renders DEGRADED —
  // empty list/grid — instead of throwing on a `.length`/`.map` access and
  // breaking the Lit studio canvas. normaliseSlide is additive + idempotent, so a
  // complete slide is byte-identical. (layoutOf already re-normalises internally;
  // it's cheap and stays correct.)
  const s = normaliseSlide(slide);
  const k = s.kind;
  let body = '';
  const bg = resolveBackground(s);
  // Resolve the cover/cta media UP FRONT (CONTRACT 5): a real (baked) image,
  // a valid video, or a chosen gradient/motion is a genuine coloured backdrop;
  // an UNRESOLVED `generate:…` img is NOT — so it must not flip has-bg/white
  // text over a non-existent image. `coverBg` therefore reads resolvedSrc, never
  // the raw bg.img.
  const coverHasGradient = bg.type === 'gradient' || bg.type === 'motion' || Boolean(bg.art);
  const coverHasMedia = Boolean(resolvedSrc(bg.img)) || Boolean(safeVideoId(bg.videoId)) || coverHasGradient;
  const layout = layoutOf(s);
  let cls = `slide k-${k} lay-${layout}` + (s.variant ? ' ' + s.variant : '');
  if (k === 'cover') cls += ' cover cover--' + coverStyleFor(layout);
  if (k === 'cta') cls += ' center';
  // has-bg (white ink) only when a real coloured backdrop is present (CONTRACT 5).
  if ((k === 'cover' || k === 'cta') && coverHasMedia) cls += ' has-bg' + ((s as { align?: string }).align === 'center' ? ' bg-center' : '');
  if (k === 'split') cls += ' split split--' + ((s as { side?: string }).side === 'right' ? 'right' : 'left');
  if (k === 'full-bleed') cls += ' full-bleed' + ((s as { align?: string }).align === 'center' ? ' fb-center' : '');
  if (k === 'quote-image') cls += ' quote-image';

  // Self-theming: stamp the slide's effective theme/font ON the slide element
  // (per-slide override, else the deck's meta) so a slide renders in its own
  // theme independent of the app chrome — and a per-slide theme works in the
  // editor AND the exported artifact. Omitted when unknown so a bare thumbnail
  // (no meta) inherits its host container's theme instead.
  const effTheme = (s.theme && THEME_IDS.includes(s.theme)) ? s.theme : (meta?.theme && THEME_IDS.includes(meta.theme) ? meta.theme : '');
  const effFont = s.font || meta?.fontset;
  const themeAttr = effTheme ? ` data-theme="${effTheme}"` : '';
  const fontAttr = (effFont === 'inter' || effFont === 'editorial') ? ` data-font="${effFont}"` : '';

  // Generic full-bleed backdrop for the non-media kinds (the data/card kinds
  // gain backgrounds): colour = a light wash (text unchanged), gradient/image/
  // motion = an art layer + scrim (text flips to on-media via .bg-media).
  let genericBg = '';
  if (!MEDIA_KINDS.has(k) && bg.type !== 'none') {
    cls += bg.type === 'colour' ? ' has-bg bg-wash' : ' has-bg bg-media';
    genericBg = slideBg(bg);
  }

  // glyph is an image-slot placeholder — suppress on an explicitly chosen gradient/motion/video
  const chosenGradient = bg.type === 'gradient' || bg.type === 'motion' || bg.type === 'video';
  const coverScrim = (cls2: string): string => coverHasMedia ? scrimEl(scrimWeight(bg, true), cls2) : '';
  // The full-bleed cover/cta backdrop (classic/centred/statement/editorial cover
  // + every cta layout): a DECORATIVE art layer (no region) shown only when a
  // real coloured backdrop is present.
  const bgLayer = coverHasMedia
    ? art({ art: bg.art, img: bg.img, videoId: bg.videoId, focal: bg.focal, animate: bg.type === 'motion' || (s as { animate?: boolean }).animate, className: 'bg-art', noGlyph: chosenGradient }) + coverScrim('bg-scrim')
    : '';

  if (k === 'cover') {
    // side-panel / split-portrait route the cover media into a right-hand panel;
    // with-image fills the FULL-BLEED bg layer. All three are IMAGE-CAPABLE
    // layouts (CONTRACT 1) so their media art() carries region:'bg' — and the
    // with-image bg layer is emitted ALWAYS (even empty) so the studio can always
    // target it, with the gradient/placeholder as the empty-state fallback
    // (CONTRACT 2). classic/centred/statement/editorial keep the decorative,
    // region-less bgLayer above.
    const sidePanelCover = layout === 'side-panel' || layout === 'split-portrait';
    const withImageCover = layout === 'with-image';
    const coverPanel = sidePanelCover
      ? `<div class="cover-panel">${art({ art: bg.art, img: bg.img, videoId: bg.videoId, focal: bg.focal, animate: bg.type === 'motion' || (s as { animate?: boolean }).animate, noGlyph: chosenGradient, region: 'bg' })}</div>`
      : '';
    // with-image: a fillable full-bleed slot (always present), scrim only when
    // there's media to veil.
    const withImageLayer = withImageCover
      ? art({ art: bg.art, img: bg.img, videoId: bg.videoId, focal: bg.focal, animate: bg.type === 'motion' || (s as { animate?: boolean }).animate, className: 'bg-art', noGlyph: chosenGradient, region: 'bg' }) + coverScrim('bg-scrim')
      : '';
    const coverBgFront = sidePanelCover ? '' : (withImageCover ? withImageLayer : bgLayer);
    body = coverBgFront +
      `<div class="top"><div class="mark">${brandMark(meta)}</div>${er('div', 'foot', 'mark', esc(s.foot || ''))}</div>` +
      `<div class="cover-mid"${build(0)}>` +
      reg('eyebrow', 'eyebrow accent', esc(s.eyebrow)) +
      `<h1${rcls('title', '')} data-region="title">${ovHtml('title', escBr(s.title))}</h1>` +
      (s.sub ? reg('sub', 'subtitle', esc(s.sub)) : '') + `</div>` +
      `<div class="bottom"><span data-region="byline" class="byline">${ovHtml('byline', esc(s.byline || ''))}</span><span class="byline"></span></div>` +
      coverPanel;
  } else if (k === 'agenda') {
    // No `active` highlight on agenda items: an authored agenda has no "current"
    // item, so the model's cross-kind `active` flag (kept for timeline) would read
    // as a stray highlighted box here. Timeline still honours `active` (see :782).
    const ol = `<ol class="agenda">` + s.items.map((it, i) =>
      `<li class="agenda-item"${build(i + 1)}><div>` +
      reg(`ag-${i}-t`, 'agenda-title', esc(it.t)) + reg(`ag-${i}-n`, 'agenda-note', esc(it.n)) + `</div></li>`).join('') + `</ol>`;
    // with-image (Option B): a real media rail in the right 40%; the CSS ::after
    // wash is the no-media fallback and is switched off when .ag-media is present.
    const agMedia = layout === 'with-image' ? sidePanel('ag-media', s) : '';
    body = titleBlock(s, { tight: true }) + ol + agMedia;
  } else if (k === 'stat-row') {
    body = titleBlock(s, { tight: true, lede: s.lede }) +
      `<div class="stat-row">` + s.stats.map((st: Stat, i) =>
        `<div class="stat"${build(i + 1)}>` +
        reg(`stat-${i}-n`, 'n', accentWrap(st.n, st.accent)) +
        reg(`stat-${i}-l`, 'label', esc(st.label)) + `</div>`).join('') + `</div>`;
  } else if (k === 'bignum') {
    body = titleBlock(s, { tight: true, lede: s.lede }) +
      `<div class="bignum">` +
      `<div${build(1)}>${reg('value', 'bignum-value', esc(s.value))}</div>` +
      `<div${build(1)}>${reg('vlabel', 'bignum-label', esc(s.vlabel))}</div>` +
      `<div class="bignum-delta${s.down ? ' down' : ''}"${build(2)}>${reg('delta', '', esc(s.delta))}</div></div>`;
  } else if (k === 'features') {
    const tb = titleBlock(s, { tight: true, accentEyebrow: true });
    const grid = `<div class="three-col">` + s.cards.map((c, i) =>
      `<div class="feature-card"${build(i + 1)}><span class="feature-icon"><svg viewBox="0 0 24 24">${FEATURE_ICONS[c.icon] || ''}</svg></span>` +
      reg(`feat-${i}-h`, '', esc(c.h)) + reg(`feat-${i}-d`, 'desc', esc(c.d)) + `</div>`).join('') + `</div>`;
    // with-lede-panel: wrap the title-block + grid in the ideal .feat-split, which
    // scopes the panel styling and disables the CSS :has() bare-markup fallback.
    body = layout === 'with-lede-panel'
      ? `<div class="feat-split">${tb}${grid}</div>`
      : tb + grid;
  } else if (k === 'chart') {
    body = titleBlock(s, { tight: true, accentEyebrow: true, lede: s.lede }) +
      `<div${build(1)} style="margin-top:8px">${chartHtml(s.bars, s.unit, s.source)}</div>`;
  } else if (k === 'quote') {
    body = titleBlock(s, { tight: true }) +
      `<blockquote${rcls('quote', 'testimonial-quote')} data-region="quote"${build(0)}>&ldquo;${ovHtml('quote', esc(s.quote))}&rdquo;</blockquote>` +
      `<div class="testimonial-by"${build(1)}>${er('span', 'avatar', 'testimonial-avatar', esc(s.avatar || ''))}<div>` +
      reg('name', 'by-name', esc(s.name)) + reg('meta', 'by-meta', esc(s.meta)) + `</div></div>`;
  } else if (k === 'bullets') {
    const list = titleBlock(s, { tight: true, lede: s.lede }) +
      `<ul class="bullets">` + s.items.map((it, i) =>
        `<li${rcls('bullet-' + i, '')} data-region="bullet-${i}"${build(i + 1)}>${ovHtml('bullet-' + i, esc(it))}</li>`).join('') + `</ul>`;
    // with-image: image rail left, title-block + bullets stacked in .bul-text right
    body = layout === 'with-image'
      ? sidePanel('bul-media', s) + `<div class="bul-text">${list}</div>`
      : list;
  } else if (k === 'split') {
    const media = `<div class="split-media">${art({ art: bg.art, img: bg.img, videoId: bg.videoId, focal: bg.focal, animate: bg.type === 'motion' || s.animate, noGlyph: bg.type === 'gradient' || bg.type === 'motion' || bg.type === 'video', region: 'bg' })}${s.caption ? er('div', 'caption', 'split-cap', esc(s.caption)) : ''}</div>`;
    const content = `<div class="split-body">` +
      reg('eyebrow', 'eyebrow accent', esc(s.eyebrow)) + `<h1 data-region="title"${rcls('title', 'title')}>${ovHtml('title', escBr(s.title))}</h1>` +
      (s.lede ? reg('lede', 'lede', esc(s.lede)) : '') +
      (s.items ? `<ul class="bullets" style="margin-top:12px">${s.items.map((it, i) => `<li${rcls('bullet-' + i, '')} data-region="bullet-${i}">${ovHtml('bullet-' + i, esc(it))}</li>`).join('')}</ul>` : '') + `</div>`;
    body = s.side === 'right' ? content + media : media + content;
  } else if (k === 'full-bleed') {
    body = art({ art: bg.art, img: bg.img, videoId: bg.videoId, focal: bg.focal, animate: bg.type === 'motion' || s.animate, className: 'fb-media', noGlyph: chosenGradient, region: 'bg' }) +
      scrimEl(scrimWeight(bg, true), 'fb-scrim') + `<div class="fb-content">` +
      reg('eyebrow', 'eyebrow accent', esc(s.eyebrow)) +
      `<h1 data-region="title"${rcls('title', 'display')}>${ovHtml('title', escBr(s.title))}</h1>` +
      (s.lede ? reg('lede', 'lede', esc(s.lede)) : '') + `</div>`;
  } else if (k === 'gallery') {
    body = titleBlock(s, { tight: true, accentEyebrow: true }) +
      `<div class="gallery-grid">` + s.items.map((it, i) =>
        `<div class="gallery-cell"${build(i + 1)}>${art({ art: it.art, img: it.img, region: `gal:${i}` })}<div class="gallery-cap">${er('div', `gal-${i}-t`, 'gallery-cap-t', esc(it.t))}${it.n ? er('div', `gal-${i}-n`, 'gallery-cap-n', esc(it.n)) : ''}</div></div>`).join('') + `</div>`;
  } else if (k === 'trend') {
    body = titleBlock(s, { tight: true, accentEyebrow: true, lede: s.lede }) +
      `<div${build(1)} style="margin-top:8px;flex:1">${trendHtml(s)}</div>` +
      (s.source ? `<div class="trend-src faint">Source: ${er('span', 'trend-src', '', esc(s.source))}</div>` : '');
  } else if (k === 'kpi') {
    const grid = `<div class="kpi-grid">` + s.kpis.map((m, i) =>
      `<div class="kpi-tile"${build(i + 1)}>${er('div', `kpi-${i}-label`, 'kpi-label', esc(m.label))}${er('div', `kpi-${i}-value`, 'kpi-value', esc(m.value))}` +
      `<div class="kpi-foot"><span class="kpi-delta ${m.up ? 'up' : 'down'}">${m.up ? '▲ ' : '▼ '}${er('span', `kpi-${i}-delta`, '', esc(m.delta))}</span>${m.spark ? sparkHtml(m.spark, m.up) : ''}</div></div>`).join('') + `</div>`;
    // with-image: a media rail left, the metrics as a single column in .kpi-body right
    body = titleBlock(s, { tight: true, accentEyebrow: true, lede: s.lede }) +
      (layout === 'with-image'
        ? `<div class="kpi-split">${sidePanel('kpi-media', s)}<div class="kpi-body">${grid}</div></div>`
        : grid);
  } else if (k === 'pricing') {
    if (layout === 'table' && s.matrix?.length) {
      // true feature-comparison matrix: a header row of tier names (+price), then
      // one row per matrix row — the row label + one cell per tier (boolean →
      // tick/cross svg; string → editable text). Reuses the documented
      // price-*-name/num/per ids in the header; rows add pmx-*-label / pmx-*-cell-*.
      const head = `<div class="pmx-row pmx-head"><div class="pmx-feat"></div>` +
        s.tiers.map((t, i) =>
          `<div class="pmx-tier${t.featured ? ' featured' : ''}">` +
          er('div', `price-${i}-name`, 'price-name', esc(t.name)) +
          `<div class="price-amount">${er('span', `price-${i}-num`, 'price-num', esc(t.price))}${t.period ? er('span', `price-${i}-per`, 'price-per', esc(t.period)) : ''}</div>` +
          `</div>`).join('') + `</div>`;
      const rows = s.matrix.map((r, ri) =>
        `<div class="pmx-row"${build(ri + 1)}>` +
        er('div', `pmx-${ri}-label`, 'pmx-feat', esc(r.label)) +
        (r.cells || []).map((c, ci) =>
          `<div class="pmx-cell${s.tiers[ci]?.featured ? ' featured' : ''}">` +
          (c === true ? `<span class="pmx-yes">${CHECK}</span>`
            : c === false ? `<span class="pmx-no">${CROSS}</span>`
              : er('span', `pmx-${ri}-cell-${ci}`, 'pmx-val', esc(String(c)))) +
          `</div>`).join('') +
        `</div>`).join('');
      body = titleBlock(s, { tight: true, accentEyebrow: true, lede: s.lede }) +
        `<div class="pmx" style="--pmx-tiers:${s.tiers.length}">${head}${rows}</div>`;
    } else {
      body = titleBlock(s, { tight: true, accentEyebrow: true, lede: s.lede }) +
        `<div class="price-grid">` + s.tiers.map((t, i) =>
          `<div class="price-card${t.featured ? ' featured' : ''}"${build(i + 1)}>` +
          (t.featured ? er('span', `price-${i}-tag`, 'price-badge', esc(t.tag || 'Most popular')) : '') +
          er('div', `price-${i}-name`, 'price-name', esc(t.name)) +
          `<div class="price-amount">${er('span', `price-${i}-num`, 'price-num', esc(t.price))}${t.period ? er('span', `price-${i}-per`, 'price-per', esc(t.period)) : ''}</div>` +
          (t.note ? er('div', `price-${i}-note`, 'price-note', esc(t.note)) : '') +
          `<ul class="price-feats">${(t.features || []).map((f, j) => `<li>${CHECK}${er('span', `price-${i}-feat-${j}`, '', esc(f))}</li>`).join('')}</ul></div>`).join('') + `</div>`;
    }
  } else if (k === 'timeline') {
    body = titleBlock(s, { tight: true, accentEyebrow: true }) +
      `<div class="timeline"><div class="timeline-line"></div>` + s.milestones.map((m, i) =>
        `<div class="tl-node${m.done ? ' done' : ''}${m.active ? ' active' : ''}"${build(i + 1)}><span class="tl-dot"></span>${er('div', `tl-${i}-date`, 'tl-date', esc(m.date))}${er('div', `tl-${i}-t`, 'tl-title', esc(m.t))}${m.n ? er('div', `tl-${i}-n`, 'tl-note', esc(m.n)) : ''}</div>`).join('') + `</div>`;
  } else if (k === 'compare') {
    const col = (c: { tag?: string; title?: string; items?: string[]; highlight?: boolean }, side: string) =>
      `<div class="cmp-col cmp-${side}${c.highlight ? ' cmp-hl' : ''}"><div class="cmp-head">${c.tag ? er('span', `cmp-${side}-tag`, 'cmp-tag', esc(c.tag)) : ''}${er('div', `cmp-${side}-title`, 'cmp-title', esc(c.title))}</div>` +
      `<ul class="cmp-list">${(c.items || []).map((it, j) => `<li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${c.highlight ? '<path d="M5 12l5 5L20 6"/>' : '<path d="M6 6l12 12M18 6L6 18"/>'}</svg>${er('span', `cmp-${side}-${j}`, '', esc(it))}</li>`).join('')}</ul></div>`;
    // s.left/s.right are backfilled to a safe empty column by normaliseSlide, but
    // guard here too so a present-but-malformed column can't slip a null through.
    body = titleBlock(s, { tight: true, accentEyebrow: true }) +
      `<div class="cmp-grid"><div${build(1)}>${col(s.left || EMPTY_COMPARE_COL, 'left')}</div><div${build(2)}>${col(s.right || EMPTY_COMPARE_COL, 'right')}</div></div>`;
  } else if (k === 'quote-image') {
    body = art({ art: bg.art, img: bg.img, videoId: bg.videoId, focal: bg.focal, animate: bg.type === 'motion' || s.animate, className: 'fb-media', noGlyph: chosenGradient, region: 'bg' }) +
      scrimEl(scrimWeight(bg, true), 'qi-scrim') + `<div class="qi-content"><span class="qi-mark">&ldquo;</span>` +
      `<blockquote${rcls('quote', 'qi-quote')} data-region="quote">${ovHtml('quote', esc(s.quote))}</blockquote>` +
      `<div class="qi-by">${s.avatar ? er('span', 'avatar', 'qi-avatar', esc(s.avatar)) : ''}<div>${er('div', 'name', 'qi-name', esc(s.name || ''))}${s.meta ? er('div', 'meta', 'qi-meta', esc(s.meta)) : ''}</div></div></div>`;
  } else if (k === 'cta') {
    body = bgLayer + `<div class="cta">` +
      reg('eyebrow', 'eyebrow accent', esc(s.eyebrow)) +
      `<h1 data-region="headline" class="cta-headline">${ovHtml('headline', esc(s.headline))}</h1>` +
      (s.sub ? `<p data-region="sub"${rcls('sub', 'cta-sub')}>${ovHtml('sub', esc(s.sub))}</p>` : '') +
      `<div class="cta-contact">${(s.contact || []).map((r, i) => `${er('span', `contact-${i}-k`, 'key', esc(r[0]))}${er('span', `contact-${i}-v`, 'val', esc(r[1]))}`).join('')}</div></div>`;
  }

  // Footer is now opt-in: only render when the slide carries explicit `footer`
  // text. No default brand line, no section tag/title (removed per request).
  const footText = (s as { footer?: string }).footer;
  const foot = (NO_FOOT.has(k) || !footText) ? '' :
    `<div class="footer-meta">${er('span', 'footer', '', esc(footText))}</div>`;
  // Optional customer logo, bottom-corner, on content (non-media) kinds. Empty
  // unless a brand-kit logo is set, so non-branded decks are unchanged.
  const brandFoot = brandFooter(meta, k);
  return `<div class="${cls}"${themeAttr}${fontAttr} data-slide-id="${esc(s.id)}" data-kind="${k}" data-layout="${layout}" data-steps="${slideSteps(s)}">${genericBg}${body}${foot}${brandFoot}</div>`;
}

/** Render the whole deck as a sequence of `.slide`s (no document chrome). The
 *  deck meta is threaded so each slide can emit the brand-kit wordmark/footer. */
export function renderSlides(
  deck: SlideDeck,
  overrides: DeckOverrides = {},
  regionStyles: DeckRegionStyles = {},
): string {
  return deck.slides.map((s) =>
    renderSlide(s, overrides[s.id] || {}, deck.meta, regionStyles[s.id] || {})).join('\n');
}

/** Inline-edit overrides keyed by slideId -> regionId -> plain/rich text. */
export type DeckOverrides = Record<string, Record<string, string>>;

/** Per-region presentation style: heading size (rt-h1..3) + text alignment. A
 *  parallel channel to overrides — these are NOT stored as tags but applied as
 *  classes by reg()/er(), so they survive sanitisation untouched. */
export interface RegionStyle { h?: number; align?: 'left' | 'center' | 'right' }
/** Region styles keyed by slideId -> regionId -> RegionStyle. */
export type DeckRegionStyles = Record<string, Record<string, RegionStyle>>;

// ── self-contained artifact document ───────────────────────────────────────
const THEMES = new Set(THEME_IDS);
function safeTheme(t?: string): string { return t && THEMES.has(t) ? t : 'workplace-paper'; }
function safeHex(h?: string): string { return h && /^#[0-9a-fA-F]{3,8}$/.test(h) ? h : ''; }
/** WCAG relative luminance → dark or light ink for content placed ON the accent
 *  colour, so a custom/customer brand of any lightness stays legible (a pale
 *  brand gets dark ink, a deep one gets cream). Mirrors slides.css fallbacks. */
function onAccentFor(hex: string): string {
  const h = hex.replace('#', '');
  const s = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.slice(0, 6);
  if (!/^[0-9a-fA-F]{6}$/.test(s)) return '#FDFCFA';
  const lin = [0, 2, 4].map((i) => {
    const v = parseInt(s.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  const L = 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
  return L > 0.45 ? '#14181F' : '#FDFCFA';
}
// ── customer branding (Phase 3) ─────────────────────────────────────────────
/** A sanitised brand kit: every user-supplied value is run through the SAME
 *  trust boundary the rest of the renderer uses — logos through srcAttr (drops
 *  javascript:/non-image schemes), the accent through safeHex, the name escaped,
 *  the surface tint clamped 0–100. Returns undefined when the deck has no
 *  brandKit (so callers can treat "no brand" as the byte-identical default). */
function safeBrandKit(meta?: { brandKit?: DeckBrand }): {
  logo: string; logoDark: string; name: string; accent: string; coBrand: boolean;
} | undefined {
  const bk = meta?.brandKit;
  if (!bk) return undefined;
  return {
    logo: srcAttr(bk.logo),                                  // '' if dropped/empty
    logoDark: srcAttr(bk.logoDark),
    name: esc(bk.name ?? ''),                                // already escaped — emit raw
    accent: safeHex(bk.accent),                              // '' if invalid
    coBrand: bk.coBrand !== false,                           // default ON
  };
}

/** The cover wordmark. With a brand-kit logo it emits an `<img class="brand-logo">`
 *  (+ a "× RICOH RDx" co-brand lockup unless coBrand === false); WITHOUT a brand
 *  logo it emits the EXISTING `RICOH RDx` literal byte-for-byte, so non-branded
 *  decks are unchanged. NB: `name` is pre-escaped by safeBrandKit. */
function brandMark(meta?: { brandKit?: DeckBrand }): string {
  const bk = safeBrandKit(meta);
  if (!bk || !bk.logo) return 'RICOH RDx';
  const img = `<img class="brand-logo" src="${bk.logo}" alt="${bk.name}">`;
  return bk.coBrand
    ? `<span class="cobrand">${img}<span class="cobrand-x">×</span><span class="cobrand-rdx">RICOH RDx</span></span>`
    : img;
}

/** A small bottom-corner customer logo for content slides — opt-in, gated on a
 *  brand logo and a non-media kind (media kinds own their full-bleed scrim). A
 *  new structural element, NOT a data-region. Empty otherwise. */
function brandFooter(meta: { brandKit?: DeckBrand } | undefined, kind: string): string {
  const bk = safeBrandKit(meta);
  if (!bk || !bk.logo || MEDIA_KINDS.has(kind)) return '';
  return `<div class="brand-foot"><img class="brand-foot-logo" src="${bk.logo}" alt="${bk.name}"></div>`;
}

/** The `:root` brand block injected into a deck document: the accent colour plus
 *  its WCAG-derived on-accent ink (slides derive --slide-on-accent from it), plus
 *  the optional brand surface tint. The brandKit.accent (when set + valid) wins
 *  over meta.brand. Empty when the deck carries no brand, so default decks are
 *  byte-identical. */
export function brandStyleVars(meta: { brand?: string; brandKit?: DeckBrand }): string {
  // Three independent axes — accent (highlights), bg (canvas), text (ink).
  // Accent NEVER colours the background.
  const accent = safeHex(meta.brandKit?.accent) || safeHex(meta.brand);
  const text = safeHex(meta.brandKit?.text);
  const bg = safeHex(meta.brandKit?.bg);
  // Default decks (no overrides) emit nothing — byte-identical.
  if (!accent && !text && !bg) return '';
  const accentVars = accent ? `--brand-color:${accent};--brand-on-accent:${onAccentFor(accent)};` : '';
  // A manual text colour overrides the theme/auto text; the secondary/tertiary
  // are translucent so they mute correctly over any canvas.
  const textVars = text
    ? `--deck-text:${text};--deck-text-2:color-mix(in srgb, ${text} 72%, transparent);--deck-text-3:color-mix(in srgb, ${text} 52%, transparent);`
    : '';
  // A custom background colour for the slide canvas (the theme canvas otherwise).
  const bgVar = bg ? `--deck-bg:${bg};` : '';
  return `:root{${accentVars}${textVars}${bgVar}}`;
}
/** Escape a JSON string for safe inlining inside a <script> element. */
function jsonForScript(v: unknown): string {
  return JSON.stringify(v ?? {})
    .replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
}

export interface DocOptions { base?: string; overrides?: DeckOverrides; regionStyles?: DeckRegionStyles }  // asset root, default "/src"

/** Compile a deck to a COMPLETE, self-contained HTML document — the canonical
 *  artifact for storage, share and export. Vanilla only: tokens/slides/present
 *  CSS + the slide-stage runtime; no framework. Boots into the presenter. */
export function renderSlidesDoc(deck: SlideDeck, opts: DocOptions = {}): string {
  const base = (opts.base ?? '/src').replace(/\/$/, '');
  const theme = safeTheme(deck.meta.theme);
  const font = safeFont(deck.meta.fontset);
  const brandVar = brandStyleVars(deck.meta);
  const deckInfo = jsonForScript({ title: deck.meta.title, notes: deck.notes || {} });
  return `<!DOCTYPE html>
<html lang="en" data-theme="${theme}" data-font="${font}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(deck.meta.title)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500&display=swap">
<link rel="stylesheet" href="${base}/tokens.css">
<link rel="stylesheet" href="${base}/slides.css">
<link rel="stylesheet" href="${base}/present.css">
<style>${brandVar}html,body{margin:0;height:100%;background:#0b0d10;font-family:var(--font-sans);}[data-slide-deck]{position:fixed;inset:0 0 auto 0;height:100vh;height:100dvh;}</style>
</head>
<body>
<div data-slide-deck id="deck"></div>
<template id="deck-slides">
${renderSlides(deck, opts.overrides || {}, opts.regionStyles || {})}
</template>
<script>window.__DECK__=${deckInfo};</script>
<script src="${base}/slide-stage.js"></script>
</body>
</html>`;
}

/** Compile a deck to a PREVIEW document — slides as a scrollable stack of
 *  scaled, hydrated `.slide`s (no presenter chrome). Used by the Studio canvas
 *  iframe so a generated deck shows immediately, styled by the same vanilla CSS.
 *  When `index` is given, only that slide is rendered (the focused canvas). */
export function renderSlidesPreviewDoc(deck: SlideDeck, opts: DocOptions & { index?: number } = {}): string {
  const base = (opts.base ?? '/src').replace(/\/$/, '');
  const theme = safeTheme(deck.meta.theme);
  const font = safeFont(deck.meta.fontset);
  const brandVar = brandStyleVars(deck.meta);
  const slides = opts.index != null && deck.slides[opts.index]
    ? [deck.slides[opts.index]]
    : deck.slides;
  const stack = slides.map((s) =>
    `<div class="slide-scaler fit-width" style="margin:0 auto 18px;max-width:1280px"><div class="slide-box"><div class="slide-mount">${renderSlide(s, (opts.overrides || {})[s.id] || {}, deck.meta, (opts.regionStyles || {})[s.id] || {})}</div></div></div>`,
  ).join('\n');
  return `<!DOCTYPE html>
<html lang="en" data-theme="${theme}" data-font="${font}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500&display=swap">
<link rel="stylesheet" href="${base}/tokens.css">
<link rel="stylesheet" href="${base}/slides.css">
<style>${brandVar}html,body{margin:0;background:var(--surface-sunken,#eee);padding:18px;font-family:var(--font-sans);}</style>
</head>
<body>
${stack}
<script src="${base}/slide-stage.js"></script>
</body>
</html>`;
}
